var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed } from "mobx";
import { DocumentRetrievalLocationStatus, DocumentRetrievalTaskSource } from "healthex-common/document.retrieval.dtos";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
export class DocumentRetrievalLoginLocationViewModel {
    constructor(parentModel) {
        this.disposer = new Disposer();
        this.parentModel = parentModel;
    }
    run() {
        this.disposer.reaction(() => {
            var _a, _b;
            return ({
                showParticleLocationBanner: this.showParticleLocationBanner,
                locationKey: (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.key,
                status: (_b = this.loginLocation) === null || _b === void 0 ? void 0 : _b.status,
            });
        }, () => this.logParticleLocationBannerDisplay(), { fireImmediately: true, name: "DocumentRetrievalLoginLocationViewModel_particleLocationBanner" });
    }
    stop() {
        this.disposer.dispose();
    }
    get loginLocation() {
        const currentLoc = this.parentModel.currentLocationForLogin;
        if (!currentLoc)
            return null;
        const location = this.parentModel.locationList.find((loc) => loc.key === currentLoc.id);
        return location !== null && location !== void 0 ? location : null;
    }
    get locationName() {
        var _a, _b;
        return (_b = (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "";
    }
    get locationLogoUrl() {
        var _a, _b;
        if (this.loginLocationIsMyChartCentral)
            return null;
        return (_b = (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.logoUrl) !== null && _b !== void 0 ? _b : null;
    }
    get loginLocationIsMyChartCentral() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.myChartCentral) !== null && _c !== void 0 ? _c : false;
    }
    get myChartCentralLocationNames() {
        return this.parentModel.locationList.filter((loc) => { var _a, _b; return (_b = (_a = loc.metadata) === null || _a === void 0 ? void 0 : _a.myChartCentral) !== null && _b !== void 0 ? _b : false; }).map((loc) => loc.name);
    }
    get locationSource() {
        var _a;
        return (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.source;
    }
    get isParticleLocation() {
        return (this.locationSource === DocumentRetrievalTaskSource.PARTICLE_EPIC ||
            this.locationSource === DocumentRetrievalTaskSource.PARTICLE_FASTEN);
    }
    get showParticleLocationBanner() {
        var _a;
        return this.isParticleLocation && ((_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.status) === DocumentRetrievalLocationStatus.NEEDS_AUTH;
    }
    get organizationName() {
        return this.parentModel.organizationName;
    }
    get isInsufficientScopeError() {
        return this.hasLoginError && this.parentModel.isInsufficientScopeError;
    }
    get title() {
        if (this.isInsufficientScopeError)
            return t("Missing MyChart Permission");
        if (this.isWaitingForAuth)
            return t("Logging in...");
        if (this.isLoginSuccessful)
            return t("Log in successful");
        return t("Log in required");
    }
    get supportingText() {
        if (this.isWaitingForAuth)
            return this.parentModel.isIAS
                ? t("If you’re having trouble or can’t remember your credentials, continue without accessing these records.")
                : t("If you’re having trouble or can’t remember your credentials, continue without sharing these records.");
        if (this.isLoginSuccessful)
            return t("Continue to keep reviewing.");
        return this.parentModel.isIAS
            ? t("Otherwise, continue without accessing these records.")
            : this.parentModel.isAIFlow
                ? t("Otherwise, continue without connecting these records.")
                : t("Otherwise, continue without sharing these records.");
    }
    get loginButtonText() {
        if (this.hasLoginError)
            return t("Try again");
        if (this.isWaitingForAuth)
            return t("Logging in...");
        if (this.isLoginSuccessful)
            return t("Continue");
        return this.parentModel.isIAS
            ? t("Log in to access records")
            : this.parentModel.isAIFlow
                ? t("Log in to connect")
                : t("Log in to share records");
    }
    get skipButtonText() {
        return t("Skip records from this provider");
    }
    async handleLoginToShare() {
        if (this.loginLocation) {
            await this.parentModel.loginToLocation(this.loginLocation.key);
        }
    }
    get isWaitingForAuth() {
        var _a;
        return ((_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.key) === this.parentModel.authInProgress;
    }
    get isLoginSuccessful() {
        if (!this.loginLocation)
            return false;
        const locationKey = this.loginLocation.key;
        const locationInList = this.parentModel.locationList.find((loc) => loc.key === locationKey);
        if (!locationInList)
            return false;
        return locationInList.status === DocumentRetrievalLocationStatus.COMPLETE && !this.isWaitingForAuth && !this.hasLoginError;
    }
    get hasLoginError() {
        var _a;
        return !!this.parentModel.authErrorKey && ((_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.key) === this.parentModel.authErrorKey;
    }
    get loginErrorMessage() {
        if (!this.hasLoginError)
            return null;
        return this.parentModel.authErrorMessage;
    }
    async handleSkipLogin() {
        await this.parentModel.onLoginSkipped();
    }
    handleContinueAfterSuccess() {
        this.parentModel.onLoginSuccess();
    }
    logParticleLocationBannerDisplay() {
        var _a, _b, _c, _d;
        if (this.showParticleLocationBanner) {
            console.log("[Document Retrieval] Particle location banner displayed", {
                locationKey: (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.key,
                locationName: this.locationName,
                source: this.locationSource,
                status: (_b = this.loginLocation) === null || _b === void 0 ? void 0 : _b.status,
                bannerText: t("You may have records here. Log in to confirm, or skip this provider if it doesn't look familiar."),
            });
            return;
        }
        console.log("[Document Retrieval] Particle location banner not displayed", {
            locationKey: (_c = this.loginLocation) === null || _c === void 0 ? void 0 : _c.key,
            locationName: this.locationName,
            source: this.locationSource,
            status: (_d = this.loginLocation) === null || _d === void 0 ? void 0 : _d.status,
            isParticleLocation: this.isParticleLocation,
        });
    }
}
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "loginLocation", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "locationName", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "locationLogoUrl", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "loginLocationIsMyChartCentral", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "myChartCentralLocationNames", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "locationSource", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "isParticleLocation", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "showParticleLocationBanner", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "organizationName", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "isInsufficientScopeError", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "title", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "supportingText", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "loginButtonText", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "skipButtonText", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "isWaitingForAuth", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "isLoginSuccessful", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "hasLoginError", null);
__decorate([
    computed
], DocumentRetrievalLoginLocationViewModel.prototype, "loginErrorMessage", null);
